// auth.interceptor.ts
import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
  HttpResponse
} from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../../features/auth/auth.service';
import { SpinnerService } from '../services/spinner.service';
import { ToasterService } from '../../shared/toaster.service';
import { StorageService, LocalStorageKey } from '../services/storage.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(private router: Router, private authService: AuthService, private spinner: SpinnerService, private toaster: ToasterService, private storage: StorageService) { }
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.url.includes('/authentication/refresh-token')) {
      return next.handle(req);
    }
    this.spinner.show();
    const token = this.storage.getLocal(LocalStorageKey.Token);
    const refreshToken = this.storage.getLocal(LocalStorageKey.RefreshToken);
    if (token && this.authService.shouldRefreshToken(token)) {

      if (!this.isRefreshing) {
        this.isRefreshing = true;
        this.refreshTokenSubject.next(null);

        return this.authService.refreshToken(refreshToken || '').pipe(
          switchMap((res: any) => {
            const accessToken = res?.data?.accessToken;
            const newRefreshToken = res?.data?.refreshToken;

            this.storage.setLocal(LocalStorageKey.Token, accessToken);
            this.storage.setLocal(LocalStorageKey.RefreshToken, newRefreshToken);

            this.isRefreshing = false;
            this.refreshTokenSubject.next(accessToken);

            const authReq = req.clone({
              setHeaders: { Authorization: `Bearer ${accessToken}` }
            });

            return next.handle(authReq);
          }),
          catchError(err => {
            this.isRefreshing = false;
            return throwError(() => err);
          })
        );
      }

      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token =>
          next.handle(
            req.clone({
              setHeaders: { Authorization: `Bearer ${token}` }
            })
          )
        )
      );
    }
    // Add token header
    const authReq = token
      ? req.clone({
        setHeaders: { Authorization: `Bearer ${token}` }
      })
      : req;

    return next.handle(authReq).pipe(
      map(event => {

        if (event instanceof HttpResponse) {

          // ✅ لو رجع 200 بس HTML
          if (event.body instanceof Blob && event.body.type.includes('text/html')) {

            const reader = new FileReader();

            reader.onload = () => {
              const text = reader.result as string;

              if (text.includes('requested URL was rejected')) {

                const match = text.match(/support ID is\s*[:]\s*([0-9]+)/i);
                const supportId = match?.[1];

                this.toaster.error(
                  supportId
                    ? `تم رفض الطلب من النظام الأمني (Support ID: ${supportId})`
                    : 'تم رفض الطلب من النظام الأمني'
                );
              }
            };

            reader.readAsText(event.body);
          }

          // الحالة القديمة لو رجع string
          else if (
            typeof event.body === 'string' &&
            event.body.includes('requested URL was rejected')
          ) {

            const match = event.body.match(/support ID is\s*[:]\s*([0-9]+)/i);
            const supportId = match?.[1];

            this.toaster.error(
              supportId
                ? `تم رفض الطلب من النظام الأمني (Support ID: ${supportId})`
                : 'تم رفض الطلب من النظام الأمني'
            );

            throw new HttpErrorResponse({
              error: event.body,
              status: 403,
              statusText: 'WAF Rejected'
            });
          }
        }

        return event;
      }),

      // ❌ Errors الطبيعية
      catchError((error: HttpErrorResponse) => {

        if (error.status === 401) {
          this.storage.removeLocal(LocalStorageKey.Token);
          this.authService.authState.next(false);
          this.router.navigate(
            ['auth/login'],
            { queryParams: { sessionExpired: true } }
          );
        }
        else if (error.error instanceof Blob && error.error.type === 'text/html') {

          const reader = new FileReader();

          reader.onload = () => {
            const text = reader.result as string;

            if (text.includes('support ID')) {
              const match = text.match(/support ID is\s*[:]\s*([0-9]+)/i);
              const supportId = match?.[1];

              this.toaster.error(
                supportId
                  ? `تم رفض الطلب من النظام الأمني (Support ID: ${supportId})`
                  : 'تم رفض الطلب من النظام الأمني'
              );
            }
          };

          reader.readAsText(error.error);
        }

        return throwError(() => error);
      }),

      finalize(() => this.spinner.hide())
    );
  }
}
